// ******************************************************************************************
// PAGE STYLE & LAYOUT
import { getPreviewChromeIconColour as getPreviewChromeIconColourFromTheme, sectionLabelStyles, UI_FONT } from "./utils/editorChromeTheme";
import {
    CANVAS_GUTTER,
    CANVAS_RADIUS,
    CHROME_FOOTER_HEIGHT,
    CHROME_HEADER_HEIGHT,
    CHROME_ICON_HIT,
    CHROME_TEXT_SIZE,
    CONTROL_RADIUS,
    pageLayoutProps,
    projectsPageContainerProps as projectsPageContainerPropsFromTokens,
} from "./utils/uiChromeTokens";

export { sectionLabelStyles };
export { getPreviewChromeIconColourFromTheme as getPreviewChromeIconColour };
export { pageLayoutProps };

export const gridBackgroundStyle = { position: "fixed", left: 0, top: 0, bottom: 0, zIndex: -1, width: "100%", height: "100vh", backgroundImage: 'url("/image/background-grid.png")', backgroundAttachment: "fixed" }

export const hiddenCanvas = { position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, overflow: 'hidden' }

/** Dark mode app / preview page background — mirrors `--mbx-background-fill` (cssVariableResolver). */
export const MBX_DARK_BACKGROUND_FILL = "#2A2A2A";

// Modal
export const modal = {
    /** Export modal tab panels: avoid `overflow: scroll` (always shows grey scrollbar tracks). */
    modalTabContents: {
        overflowX: "hidden",
        overflowY: "auto",
        maxHeight: "min(520px, calc(100vh - 200px))",
    },
};

/** Editor modals (left nav, image library) — match aside: 8px radius, light border, amber accent (tabs); primary CTAs are black. */
export const editorModalAccent = "var(--mbx-modal-accent, #fe9a00)";
export const editorModalAccentHover = "var(--mbx-modal-accent-hover, #d97b00)";
export const editorModalBorder = "var(--mbx-modal-border, #e7e5e4)";

/** Shared by AppNavBar (subscribe bar + title row) and ProjectsPage so horizontal edges line up. */
export const projectsPageContainerProps = projectsPageContainerPropsFromTokens;

export const editorModalShell = {
    radius: CONTROL_RADIUS,
    overlayProps: { backgroundOpacity: 0.35, blur: 2 },
    centered: true,
    styles: {
        content: {
            border: `1px solid ${editorModalBorder}`,
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)",
        },
        header: {
            borderBottom: `1px solid ${editorModalBorder}`,
            padding: "8px 12px",
            marginBottom: 0,
            backgroundColor: "var(--mbx-modal-header-bg, #ffffff)",
        },
        title: {
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--mbx-modal-muted-text, #6b7280)",
        },
        body: { padding: 0 },
        close: { color: "var(--mbx-modal-muted-text, #6b7280)" },
    },
};

/** Stix Primary button — black fill, white text, Inter 13px. */
export const editorModalPrimaryButtonStyles = {
    root: {
        backgroundColor: "#1C1917",
        color: "#ffffff",
        borderRadius: CONTROL_RADIUS,
        fontSize: CHROME_TEXT_SIZE,
        paddingLeft: 12,
        paddingRight: 12,
        fontFamily: UI_FONT,
        fontWeight: 500,
        height: 30,
        minHeight: 30,
        "&:hover": { backgroundColor: "#292524" },
    },
    label: { color: "#ffffff" },
};

/** Stix Secondary button — surface bg, stone border, Inter 13px. */
export const editorModalSecondaryButtonStyles = {
    root: {
        backgroundColor: "var(--mbx-modal-standard-fill, #ffffff)",
        color: "var(--mbx-modal-primary-text, #1c1917)",
        border: "1px solid var(--mbx-modal-border, #e7e5e4)",
        borderRadius: CONTROL_RADIUS,
        fontSize: CHROME_TEXT_SIZE,
        paddingLeft: 12,
        paddingRight: 12,
        fontFamily: UI_FONT,
        fontWeight: 500,
        height: 30,
        minHeight: 30,
        "&:hover": {
            backgroundColor: "var(--mbx-modal-muted-fill, #f5f5f4)",
            borderColor: "var(--mbx-modal-border, #d6d3d1)",
        },
    },
    label: { color: "var(--mbx-modal-primary-text, #1c1917)" },
};

export const editorModalPrimaryButtonCompactStyles = {
    root: {
        backgroundColor: "#1C1917",
        color: "#ffffff",
        borderRadius: CONTROL_RADIUS,
        height: 26,
        minHeight: 26,
        fontSize: 12,
        paddingLeft: 10,
        paddingRight: 10,
        fontFamily: UI_FONT,
        fontWeight: 500,
        "&:hover": { backgroundColor: "#292524" },
    },
    label: { color: "#ffffff" },
};

/** Framed icon targets inside the storyboard toolbar */
export const storyboardToolbarIconStyles = (hover, isDark = false) =>
    isDark
        ? {
              root: {
                  width: CHROME_ICON_HIT,
                  height: CHROME_ICON_HIT,
                  minWidth: CHROME_ICON_HIT,
                  minHeight: CHROME_ICON_HIT,
                  borderRadius: CONTROL_RADIUS,
                  border: "1px solid transparent",
                  backgroundColor: hover ? "#333333" : "transparent",
                  color: hover ? "#FAFAF9" : "#A8A29E",
                  transition: "background-color 120ms ease, color 120ms ease",
              },
          }
        : {
              root: {
                  width: CHROME_ICON_HIT,
                  height: CHROME_ICON_HIT,
                  minWidth: CHROME_ICON_HIT,
                  minHeight: CHROME_ICON_HIT,
                  borderRadius: CONTROL_RADIUS,
                  border: "1px solid transparent",
                  backgroundColor: hover ? "#F5F5F4" : "transparent",
                  color: hover ? "#1C1917" : "#78716C",
                  transition: "background-color 120ms ease, color 120ms ease",
              },
          };

/** Storyboard scene cards — pill CTAs */
export const storyboardSceneButtonStyles = {
    root: {
        backgroundColor: "#1C1917",
        color: "#ffffff",
        borderRadius: CONTROL_RADIUS,
        height: 30,
        minHeight: 30,
        fontSize: CHROME_TEXT_SIZE,
        paddingLeft: 12,
        paddingRight: 12,
        fontWeight: 500,
        fontFamily: UI_FONT,
        "&:hover": { backgroundColor: "#292524" },
    },
    label: { color: "#ffffff" },
};

/**
 * Storyboard listing + template picker cards — aligned with editor chrome via viewportThemeOverride.
 * Light surfaces mirror the storyboard page; dark matches editor navbar / aside (#2f2f2f) and main fill (MBX_DARK_BACKGROUND_FILL).
 */
export function getStoryboardChromeTheme(isDark) {
    if (isDark) {
        return {
            stackBg: MBX_DARK_BACKGROUND_FILL,
            headerBg: "#2A2A2A",
            mainBg: MBX_DARK_BACKGROUND_FILL,
            cardBg: "#2A2A2A",
            cardBorder: "#44403C",
            cardEmphasisBorder: "#78716C",
            thumbBg: "#333333",
            thumbInsetShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.1)",
            thumbPlaceholderGradient: "linear-gradient(180deg, #44403c 0%, #333333 100%)",
            titleColor: "#FAFAF9",
            bodyTextColor: "#D6D3D1",
            mutedTextColor: "#A8A29E",
            sectionLabelColor: "#A8A29E",
            headlineColor: "#FAFAF9",
            footnoteBg: "#292524",
            cardContentMutedBg: "#292524",
            hoverShadow: "none",
        };
    }
    return {
        stackBg: "#FAFAF9",
        headerBg: "#FFFFFF",
        mainBg: "#FAFAF9",
        cardBg: "#FFFFFF",
        cardBorder: "#E7E5E4",
        cardEmphasisBorder: "#D6D3D1",
        thumbBg: "#F5F5F4",
        thumbInsetShadow: "inset 0 0 0 1px rgba(28, 25, 23, 0.08)",
        thumbPlaceholderGradient: "linear-gradient(180deg, #f5f5f4 0%, #e7e5e4 100%)",
        titleColor: "#1C1917",
        bodyTextColor: "#57534E",
        mutedTextColor: "#78716C",
        sectionLabelColor: "#78716C",
        headlineColor: "#1C1917",
        footnoteBg: "#F5F5F4",
        cardContentMutedBg: "#F5F5F4",
        hoverShadow: "none",
    };
}

/** 16:9 preview — shared by storyboard scene cards and template modal cards */
export function storyboardCardThumbStyleFromTheme(theme) {
    return {
        width: "100%",
        aspectRatio: "16 / 9",
        flexShrink: 0,
        backgroundColor: theme.thumbBg,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        borderBottom: `1px solid ${theme.cardBorder}`,
        boxShadow: theme.thumbInsetShadow,
    };
}

export function storyboardCardRootStyleFromTheme(theme) {
    return {
        border: `1px solid ${theme.cardEmphasisBorder ?? theme.cardBorder}`,
        overflow: "hidden",
        backgroundColor: theme.cardBg,
        transition: "box-shadow 150ms ease",
    };
}

/** Content block under the 16:9 thumb — same muted panel on storyboard + template cards */
export function storyboardCardMutedContentSectionStyle(theme) {
    return {
        backgroundColor: theme.cardContentMutedBg,
        borderTop: `1px solid ${theme.cardBorder}`,
    };
}

/** Mantine Menu when anchored to the editor left rail — open to the right, not over the icons */
export const leftNavMenuProps = {
    position: "right-start",
    offset: 8,
};

/** Mantine Tabs styles — underline active tab like Editor aside */
export const editorModalTabsStyles = {
    list: {
        borderBottom: `1px solid ${editorModalBorder}`,
        gap: 0,
        height: 34,
        paddingLeft: 8,
        paddingRight: 8,
        backgroundColor: "#ffffff",
        marginBottom: 0,
    },
    tab: {
        fontSize: CHROME_TEXT_SIZE,
        fontWeight: 400,
        padding: "0 12px",
        height: 34,
        border: "none",
        borderRadius: `${CONTROL_RADIUS}px ${CONTROL_RADIUS}px 0 0`,
        borderBottom: "2px solid transparent",
        marginBottom: -1,
        color: "var(--mbx-modal-primary-text, #111827)",
        backgroundColor: "transparent",
        '&[data-active]': {
            borderBottomColor: editorModalAccent,
            color: "var(--mbx-modal-primary-text, #111827)",
        },
    },
    panel: { padding: 0 },
};

/** Mantine Modal `styles` — stronger title (matches Generate with AI). */
export const editorModalStrongTitleStyles = {
    ...editorModalShell.styles,
    title: {
        ...editorModalShell.styles.title,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "0.07em",
        color: "var(--mbx-modal-title-text, #111827)",
    },
};

/** Top gallery / picker strip inside editor modals */
export const editorModalGalleryPanelStyle = {
    width: "100%",
    padding: "12px 16px",
    maxHeight: 240,
    overflow: "auto",
    backgroundColor: "var(--mbx-modal-gallery-bg, #f9fafb)",
    backgroundImage: "none",
    backgroundSize: "auto",
    borderBottom: `1px solid ${editorModalBorder}`,
};

export const editorModalGalleryPanelStyleTall = {
    ...editorModalGalleryPanelStyle,
    maxHeight: 520,
};

export const editorModalThumbFrameStyle = {
    width: "100%",
    aspectRatio: "1",
    borderRadius: CONTROL_RADIUS,
    overflow: "hidden",
    border: `1px solid ${editorModalBorder}`,
    backgroundColor: "var(--mbx-modal-thumb-bg, #ffffff)",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
};

export const editorModalFootnoteBoxStyle = {
    backgroundColor: "var(--mbx-modal-footnote-bg, #f9fafb)",
    borderRadius: CONTROL_RADIUS,
    border: `1px solid ${editorModalBorder}`,
};

export const editorModalMdTextInputStyles = {
    input: {
        borderColor: editorModalBorder,
        height: 30,
        minHeight: 30,
        "&:focus": { borderColor: editorModalAccent },
    },
};

export const editorModalTextareaStyles = {
    input: {
        borderColor: editorModalBorder,
        "&:focus": { borderColor: editorModalAccent },
    },
};

export const editorModalPrimaryButtonStylesMd = {
    ...editorModalPrimaryButtonStyles,
    root: {
        ...editorModalPrimaryButtonStyles.root,
        height: 42,
        minHeight: 42,
    },
};
 
/**
 * Preview `PreviewChrome` / top bar icon glyphs — aligns with `getEditorChromeTheme`.
 */
// getPreviewChromeIconColour re-exported from editorChromeTheme.js (top of file)

//Navbar
export const toolbar = {
    toolbarContainer: {
        minHeight: CHROME_HEADER_HEIGHT,
        display: "flex",
        alignItems: "center",
        width: "100%",
        boxSizing: "border-box",
        paddingLeft: 20,
        paddingRight: 20,
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
        backgroundColor: "var(--mbx-top-appbar-bg)",
        borderBottom: "1px solid var(--mbx-top-appbar-border)",
    },
    toolbarActionIcon: {
        marginLeft: 10, height: 25
    },
    toolbarActionIconContainer: {
        display: 'flex',
        alignItems: 'center',
        height: "100%",
        justifyContent: 'flex-start',
        margin: "2.5px",
        paddingBottom: 0.5
    },
    toolbarCentralGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        borderRadius: `${CONTROL_RADIUS}px`,
        border: 'none',
        height: 32,
        paddingLeft: 0,
        paddingRight: 0,
    }
}

/** Fixed preview chrome: match editor header + realtime footer heights. */
export const previewChromeTopPx = CHROME_HEADER_HEIGHT;
export const previewChromeBottomPx = CHROME_FOOTER_HEIGHT;

/** Gutter (px) between preview chrome and the canvas — matches editor viewport inset. */
export const previewCanvasGutterPx = CANVAS_GUTTER;

/** Border radius (px) for the preview canvas frame — matches editor. */
export const previewCanvasFrameRadiusPx = CANVAS_RADIUS;

const previewCanvasFrameTopPx = previewChromeTopPx + previewCanvasGutterPx;
const previewCanvasFrameBottomPx = previewChromeBottomPx + previewCanvasGutterPx;

/** CSS `top` for the vertical center of the framed preview canvas (fallback when canvas rect is unknown). */
export const previewCanvasCenterTopCalc = `calc(${previewCanvasFrameTopPx}px + (100vh - ${previewCanvasFrameTopPx + previewCanvasFrameBottomPx}px) / 2)`;

/** Fixed outer frame: margins vs header/footer + rounded corners; WebGL fills inside via `previewCanvasWebGlStyle`. */
export const previewCanvasFrameStyle = {
  position: "fixed",
  zIndex: 1,
  top: `${previewCanvasFrameTopPx}px`,
  left: `${previewCanvasGutterPx}px`,
  right: `${previewCanvasGutterPx}px`,
  bottom: `${previewCanvasFrameBottomPx}px`,
  borderRadius: previewCanvasFrameRadiusPx,
  overflow: "hidden",
  backgroundColor: "var(--mbx-background-fill)",
};

/** R3F `<Canvas>` — fills a `previewCanvasFrameStyle` wrapper. */
export const previewCanvasWebGlStyle = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  display: "block",
};

/** Alias for loaders / legacy call sites; same as `previewCanvasFrameStyle`. */
export const previewCanvasInsetStyle = previewCanvasFrameStyle;

// ******************************************************************************************
// Projects page
export const projectStyle = {
    container: {
        slimFormContainer: {
            margin: 0,
            padding: 0
        },
        loadingSpinnerContainer: {
            width: "50px",
            height: "50px"
        },
        projectScreenshotContainer: {
            overflow: "hidden",
            position: "relative",
            width: "100%",
            height: "100%",
            minHeight: 140,
            top: 0,
            left: 0,
        }
    },
    box: {
        projectScreenshotBox: {
            top: 0,
            left: 0,
            width: "100%",
            aspectRatio: "5 / 3",
            minHeight: 120,
            maxHeight: 180,
            overflow: "hidden",
            backgroundColor: "#f1f5f9",
            borderBottom: `1px solid ${editorModalBorder}`,
        },
        projectTextBox: {
            width: "100%",
            paddingLeft: 12,
            paddingRight: 12,
            paddingTop: 8,
            margin: 0,
        }
    },
    projectButton: {
        newProjectButton: {
            active: {
                backgroundColor: "#FAC710",
                boxShadow: "none",
                color: 'black',
                borderRadius: 0,
                borderStyle: "outset",
                padding: 10
            },
            disabled: { 
                backgroundColor: "grey",
                boxShadow: "none",
                color: 'white',
                borderRadius: 0,
                borderStyle: "outset",
                padding: 10
            }
        },
        editProjectButton: {
            width: '60%',
            margin: 10,
            inline: true,
            backgroundColor: 'black',
            borderRadius: 4
        },
        upgradePlaceholderButton: {
            width: '60%',
            margin: 10,
            inline: true,
            backgroundColor: 'gray',
            borderRadius: 4
        }
    },
}

// ******************************************************************************************
// Editor
export const editor = {
    // SIDEBAR
    sidebarAccordion: {
        opacity: 0.5,
        height: "100%",
        top: 0,
        right: 295,
        position: 'absolute',
    },
    sidebarContainer: {
        height: "100vh", gap: 0, justifyContent: 'space-between'
    },
    sidebarSection: {
        overflowY: "scroll",
        scrollbarWidth: 'none',
        // boxShadow: 'inset 0 -10px 10px -10px rgba(0, 0, 0, 0.5)'
    },
    sidebarIcon: {
        width: '16px', height: '16px', borderBottom: '#F9F9F9', marginRight: 5
    },
    /** Sidebar open/close control — absolutely positioned vs. Tabs (parent needs position: relative) */
    modelAccordion: {
        position: "absolute",
        top: "50%",
        zIndex: 10,
        width: "auto",
        height: "auto",
    },
    sidebarOthersContainer: {
        width: "100%", paddingRight: 15, padding: 5, marginLeft: 5, marginTop: 10
    },
    sidebarOthersUsers: {
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "none",
    },
    /** ~3 collaborator rows at current OtherUser row height; list scrolls beyond this */
    sidebarOthersUsersMaxHeight: 132,

    // ARTBOARD PANEL
    artboardTopLevelContainer: {
        width: "100%", height:'100%'
    },
    artboardCard: { borderRadius: 4, cursor: 'pointer' },
    artboardCardContents: { position: "absolute"},
    artboardViewingScreenshot: { width: 120, height: 85 },
    artboardNotViewingScreenshot: { width: 120, height: 85, borderRadius: 0, backgroundColor: '#C1C2C5' },
    artboardNameText: { textOverflow: 'ellipsis', overflow: "hidden", whiteSpace: 'nowrap', marginBottom: 10, width: 95 },
    artboardActionIcon: { marginRight: 10, height: 20, width: 20 }

}
